import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Monitor, Share } from 'lucide-react';
import { Button } from './ui/button';

export default function PWAInstallPrompt() {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if banner was dismissed
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismiss = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Show banner if not installed, not dismissed recently (7 days), and not in standalone
    if (!standalone && daysSinceDismiss > 7) {
      // For iOS, show banner after a delay
      if (iOS) {
        setTimeout(() => setShowBanner(true), 3000);
      }
    }

    // Listen for beforeinstallprompt (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show banner after delay if not recently dismissed
      if (daysSinceDismiss > 7) {
        setTimeout(() => setShowBanner(true), 2000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      setShowBanner(false);
      setDeferredPrompt(null);
      console.log('[PWA] App installed successfully');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Show native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('[PWA] User choice:', outcome);
      
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  // Don't show if already installed
  if (isStandalone || !showBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-5 duration-500">
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden max-w-lg mx-auto">
        {/* Header with close button */}
        <div className="flex items-center justify-between p-4 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
              <img src="/icon-72.png" alt="BIGBUS" className="w-8 h-8 rounded" />
            </div>
            <div>
              <h3 className="font-bold text-white text-lg">BIGBUS</h3>
              <p className="text-slate-400 text-sm">Установите приложение</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-slate-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-4">
          {isIOS ? (
            // iOS instructions
            <div className="space-y-3">
              <p className="text-slate-300 text-sm">
                Добавьте приложение на главный экран:
              </p>
              <div className="flex items-center gap-3 text-sm text-slate-400 bg-slate-800/50 rounded-lg p-3">
                <Share className="w-5 h-5 text-blue-400 shrink-0" />
                <span>Нажмите <strong className="text-white">Поделиться</strong> → <strong className="text-white">На экран «Домой»</strong></span>
              </div>
              <Button
                onClick={handleDismiss}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Понятно
              </Button>
            </div>
          ) : (
            // Android/Desktop install
            <div className="space-y-3">
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <div className="flex items-center gap-1.5">
                  <Smartphone className="w-4 h-4" />
                  <span>Android</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Monitor className="w-4 h-4" />
                  <span>Windows</span>
                </div>
              </div>
              <Button
                onClick={handleInstall}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2"
              >
                <Download className="w-4 h-4" />
                Установить приложение
              </Button>
              <button
                onClick={handleDismiss}
                className="w-full text-slate-500 hover:text-slate-300 text-sm py-2 transition-colors"
              >
                Не сейчас
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
